import {
	Alert,
	Box,
	Card,
	createStyles,
	Group,
	Stack,
	Textarea,
} from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import type {
	BackgroundJobOut,
	CondenseEffort,
	LiteWorkspace,
	WorkspaceMemoryCondenseOut,
	WorkspaceMemoryOut,
} from '@repo/api-codegen';
import {
	getWorkspaceMemoryQueryKey,
	useApplyCondensedWorkspaceMemory,
	useGetCondensedWorkspaceMemoryPreview,
	useGetWorkspaceMemory,
	usePreviewCondensedWorkspaceMemory,
} from '@repo/api-codegen';
import { Button, Select, Text, Title } from '@repo/foundations';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import type { IBackgroundJob } from '../../../api/types/models/backgroundJob';
import { BackgroundJobProgress } from '../../../components/BackgroundJobProgress';
import FullWidthLoadingSpinner from '../../../components/LoadingSpinner';
import type { BackgroundJob } from '../../../lib/models/backgroundJob';
import { saveBlob } from '../../../lib/models/misc';
import { SuperuserWorkspaceSelector } from '../components/SuperuserWorkspaceSelector';
import { buildWorkspaceMemorySideBySideDiffRows } from './workspaceMemoryDiff';

const CONDENSE_EFFORT_OPTIONS: { label: string; value: CondenseEffort }[] = [
	{ label: 'Low', value: 'low' },
	{ label: 'Medium', value: 'medium' },
	{ label: 'High', value: 'high' },
];

const useStyles = createStyles((theme) => ({
	diffContent: {
		overflowWrap: 'anywhere',
		padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	diffLineNumber: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
		borderRight: `1px solid ${theme.other.getColor('border/primary/default')}`,
		color: theme.other.getColor('text/secondary/default'),
		padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
		textAlign: 'right',
		userSelect: 'none',
		verticalAlign: 'top',
		width: theme.other.space[12],
	},
	diffCellAdded: {
		backgroundColor: theme.other.getColor('surface/success/default'),
	},
	diffCellEmpty: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
	},
	diffCellOmitted: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
		color: theme.other.getColor('text/secondary/default'),
	},
	diffCellRemoved: {
		backgroundColor: theme.other.getColor('surface/critical/default'),
	},
	diffHeader: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
		borderBottom: `1px solid ${theme.other.getColor('border/primary/default')}`,
		fontWeight: theme.other.typography.weight.semibold,
		padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
		textAlign: 'left',
	},
	diffLineNumberHeader: {
		backgroundColor: theme.other.getColor('fill/secondary/default'),
		borderBottom: `1px solid ${theme.other.getColor('border/primary/default')}`,
		width: theme.other.space[12],
	},
	diffSign: {
		color: theme.other.getColor('text/secondary/default'),
		padding: `${theme.spacing.xs} 0`,
		textAlign: 'center',
		userSelect: 'none',
		verticalAlign: 'top',
		width: theme.other.space[4],
	},
	diffTable: {
		borderCollapse: 'collapse',
		fontFamily:
			'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
		fontSize: theme.fontSizes.xs,
		tableLayout: 'fixed',
		width: '100%',
	},
	diffTableDivider: {
		borderLeft: `1px solid ${theme.other.getColor('border/primary/default')}`,
	},
	diffWrapper: {
		border: `1px solid ${theme.other.getColor('border/primary/default')}`,
		borderRadius: theme.radius.md,
		maxWidth: '100%',
		overflow: 'hidden',
	},
	memoryEditor: {
		'.mantine-Textarea-input': {
			fontFamily:
				'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
			fontSize: theme.fontSizes.xs,
			whiteSpace: 'pre-wrap',
		},
	},
}));

function InspectWorkspaceMemoryPage() {
	const { classes } = useStyles();
	const [workspace, setWorkspace] = useState<LiteWorkspace | undefined>(
		undefined
	);
	const [effort, setEffort] = useState<CondenseEffort>('medium');
	const [condensePreview, setCondensePreview] = useState<
		WorkspaceMemoryCondenseOut | undefined
	>(undefined);
	const [editedMemoryDraft, setEditedMemoryDraft] = useState<
		string | undefined
	>(undefined);
	const [previewJob, setPreviewJob] = useState<BackgroundJobOut | undefined>(
		undefined
	);
	const queryClient = useQueryClient();

	const {
		data: memoryData,
		isLoading,
		error,
		refetch,
	} = useGetWorkspaceMemory(
		{
			pathParams: {
				workspaceId: workspace?.id || '',
			},
		},
		{
			enabled: false, // Don't auto-fetch, only fetch on manual trigger
		}
	);

	const handleSetWorkspace = useCallback((newWorkspace?: LiteWorkspace) => {
		setWorkspace(newWorkspace);
		setCondensePreview(undefined);
		setEditedMemoryDraft(undefined);
		setPreviewJob(undefined);
	}, []);

	const handleFetchMemory = useCallback(() => {
		if (workspace?.id) {
			setCondensePreview(undefined);
			setEditedMemoryDraft(undefined);
			setPreviewJob(undefined);
			refetch();
		}
	}, [workspace?.id, refetch]);

	const previewCondenseMutation = usePreviewCondensedWorkspaceMemory({
		onSuccess: (job) => {
			setPreviewJob(job);
			showNotification({
				title: 'Preview started',
				message:
					'Workspace memory condensation preview is running in the background.',
				color: 'blue',
				autoClose: 3000,
			});
		},
		onError: (condenseError) => {
			showNotification({
				title: 'Error',
				message: `Failed to generate preview: ${condenseError?.detail || 'Unknown error'}`,
				color: 'red',
				autoClose: 5000,
			});
		},
	});

	const previewJobQuery = useGetCondensedWorkspaceMemoryPreview(
		{
			pathParams: {
				workspaceId: workspace?.id || '',
				jobId: previewJob?.id || '',
			},
		},
		{
			enabled: false,
		}
	);
	const currentCondensePreview =
		condensePreview ?? previewJobQuery.data?.preview;

	const applyCondenseMutation = useApplyCondensedWorkspaceMemory({
		onSuccess: (updatedMemory: WorkspaceMemoryOut) => {
			if (workspace?.id) {
				queryClient.setQueryData(
					getWorkspaceMemoryQueryKey({
						pathParams: {
							workspaceId: workspace.id,
						},
					}),
					updatedMemory
				);
			}
			setCondensePreview(undefined);
			showNotification({
				title: 'Memory condensed',
				message: 'Workspace memory has been updated.',
				color: 'green',
				autoClose: 3000,
			});
		},
		onError: (applyError) => {
			showNotification({
				title: 'Error',
				message: `Failed to apply condensed memory: ${applyError?.detail || 'Unknown error'}`,
				color: 'red',
				autoClose: 5000,
			});
		},
	});

	const saveMemoryMutation = useApplyCondensedWorkspaceMemory({
		onSuccess: (updatedMemory: WorkspaceMemoryOut) => {
			if (workspace?.id) {
				queryClient.setQueryData(
					getWorkspaceMemoryQueryKey({
						pathParams: {
							workspaceId: workspace.id,
						},
					}),
					updatedMemory
				);
			}
			setEditedMemoryDraft(undefined);
			setCondensePreview(undefined);
			showNotification({
				title: 'Memory saved',
				message: 'Workspace memory has been updated.',
				color: 'green',
				autoClose: 3000,
			});
		},
		onError: (saveError) => {
			showNotification({
				title: 'Error',
				message: `Failed to save memory: ${saveError?.detail || 'Unknown error'}`,
				color: 'red',
				autoClose: 5000,
			});
		},
	});

	const handleEffortChange = useCallback((newEffort: string | null) => {
		if (!newEffort) {
			return;
		}

		setEffort(newEffort as CondenseEffort);
		setCondensePreview(undefined);
		setPreviewJob(undefined);
	}, []);

	const handleGenerateCondensePreview = useCallback(() => {
		if (!workspace?.id) {
			return;
		}

		setCondensePreview(undefined);
		setPreviewJob(undefined);
		previewCondenseMutation.mutate({
			pathParams: {
				workspaceId: workspace.id,
			},
			body: {
				effort,
			},
		});
	}, [effort, previewCondenseMutation, workspace?.id]);

	const handleApplyCondensedMemory = useCallback(() => {
		if (!workspace?.id || !currentCondensePreview) {
			return;
		}

		applyCondenseMutation.mutate({
			pathParams: {
				workspaceId: workspace.id,
			},
			body: {
				effort: currentCondensePreview.effort,
				condensed_memory: currentCondensePreview.condensed_memory,
			},
		});
	}, [applyCondenseMutation, currentCondensePreview, workspace?.id]);

	const handleDownloadMemory = useCallback(() => {
		if (!memoryData) {
			return;
		}

		saveBlob(
			new Blob([memoryData.memory], {
				type: 'text/markdown;charset=utf-8',
			}),
			`${memoryData.workspace_id}-workspace-memory.md`
		);
	}, [memoryData]);

	const handleSaveMemory = useCallback(() => {
		if (!workspace?.id || !memoryData) {
			return;
		}

		saveMemoryMutation.mutate({
			pathParams: {
				workspaceId: workspace.id,
			},
			body: {
				effort,
				condensed_memory: editedMemoryDraft ?? memoryData.memory,
			},
		});
	}, [
		editedMemoryDraft,
		effort,
		memoryData,
		saveMemoryMutation,
		workspace?.id,
	]);

	const handlePreviewJobComplete = useCallback(
		async (job?: IBackgroundJob) => {
			if (job?.failed) {
				setPreviewJob(undefined);
				showNotification({
					title: 'Error',
					message: job.logs || 'Failed to generate preview.',
					color: 'red',
					autoClose: 5000,
				});
				return;
			}

			if (!job?.completed) {
				return;
			}

			const result = await previewJobQuery.refetch();
			if (result.data?.preview) {
				setCondensePreview(result.data.preview);
				setPreviewJob(undefined);
				showNotification({
					title: 'Preview generated',
					message: 'Workspace memory condensation preview is ready.',
					color: 'green',
					autoClose: 3000,
				});
			}
		},
		[previewJobQuery]
	);

	const isGeneratingCondensePreview =
		previewCondenseMutation.isPending || Boolean(previewJob);
	const hasMemoryEdits = memoryData
		? editedMemoryDraft !== undefined && editedMemoryDraft !== memoryData.memory
		: false;
	const editedMemory = editedMemoryDraft ?? memoryData?.memory ?? '';
	const diffRows = useMemo(
		() =>
			currentCondensePreview
				? buildWorkspaceMemorySideBySideDiffRows(
						memoryData?.memory ?? '',
						currentCondensePreview.condensed_memory
					)
				: [],
		[currentCondensePreview, memoryData?.memory]
	);

	return (
		<Stack spacing="xl">
			<Stack w={500}>
				<Text size="sm" color="text/secondary/default">
					View the AI memory content for any workspace. This memory contains
					advanced context and learned patterns that the AI uses across
					conversations.
				</Text>

				<Group>
					<Title order={5}>Workspace</Title>
					<SuperuserWorkspaceSelector
						selected={workspace}
						setSelected={handleSetWorkspace}
					/>
				</Group>

				{workspace && (
					<Stack spacing="sm">
						<Text size="sm">
							<strong>Workspace ID:</strong> {workspace.id}
						</Text>
						<Text size="sm">
							<strong>Workspace Name:</strong> {workspace.name}
						</Text>
						<Button
							variant="primary"
							size="md"
							onClick={handleFetchMemory}
							loading={isLoading}
							disabled={!workspace}
						>
							Fetch Memory
						</Button>
					</Stack>
				)}
			</Stack>

			{isLoading && <FullWidthLoadingSpinner />}

			{error && (
				<Alert title="Error" color="red">
					{error.detail || 'Failed to fetch workspace memory'}
				</Alert>
			)}

			{memoryData && (
				<Stack spacing="md">
					<Title order={3}>Memory Details</Title>

					<Stack spacing="sm">
						<Text size="sm">
							<strong>Workspace ID:</strong> {memoryData.workspace_id}
						</Text>
						<Text size="sm">
							<strong>Memory Size:</strong> {memoryData.size_mb} MB (
							{memoryData.size_bytes.toLocaleString()} bytes)
						</Text>
					</Stack>

					<Card withBorder>
						<Stack spacing="sm">
							<Title order={4}>Condense Memory</Title>
							<Text size="sm" color="text/secondary/default">
								Generate a preview before applying. High effort is more
								aggressive and will remove more detail than low effort.
							</Text>
							<Group align="flex-end">
								<Select
									label="Effort"
									value={effort}
									onChange={handleEffortChange}
									data={CONDENSE_EFFORT_OPTIONS}
								/>
								<Button
									variant="primary"
									size="md"
									onClick={handleGenerateCondensePreview}
									loading={isGeneratingCondensePreview}
									disabled={!workspace || !memoryData}
								>
									Condense
								</Button>
							</Group>
						</Stack>
					</Card>

					{previewJob && (
						<BackgroundJobProgress
							key={previewJob.id}
							job={previewJob as unknown as BackgroundJob}
							onCompleted={handlePreviewJobComplete}
						/>
					)}

					{currentCondensePreview && (
						<Card withBorder>
							<Stack spacing="md">
								<Group position="apart">
									<Title order={4}>Condense Preview</Title>
									<Button
										variant="primary"
										size="md"
										onClick={handleApplyCondensedMemory}
										loading={applyCondenseMutation.isPending}
										disabled={applyCondenseMutation.isPending}
									>
										Apply
									</Button>
								</Group>
								<Group>
									<Text size="sm">
										<strong>Original Size:</strong>{' '}
										{currentCondensePreview.original_size_mb} MB
									</Text>
									<Text size="sm">
										<strong>New Size:</strong>{' '}
										{currentCondensePreview.condensed_size_mb} MB
									</Text>
									<Text size="sm">
										<strong>Projected Reduction:</strong>{' '}
										{currentCondensePreview.reduction_percent}%
									</Text>
									<Text size="sm">
										<strong>Effort:</strong> {currentCondensePreview.effort}
									</Text>
								</Group>
								<Box className={classes.diffWrapper}>
									<table className={classes.diffTable}>
										<thead>
											<tr>
												<th className={classes.diffLineNumberHeader} />
												<th className={classes.diffHeader}>Before</th>
												<th
													className={`${classes.diffLineNumberHeader} ${classes.diffTableDivider}`}
												/>
												<th className={classes.diffHeader}>After</th>
											</tr>
										</thead>
										<tbody>
											{diffRows.map((row, index) => {
												const beforeCellClassName =
													row.before.kind === 'removed'
														? classes.diffCellRemoved
														: row.before.kind === 'omitted'
															? classes.diffCellOmitted
															: row.before.kind === 'empty'
																? classes.diffCellEmpty
																: undefined;
												const afterCellClassName =
													row.after.kind === 'added'
														? classes.diffCellAdded
														: row.after.kind === 'omitted'
															? classes.diffCellOmitted
															: row.after.kind === 'empty'
																? classes.diffCellEmpty
																: undefined;
												const beforeSign =
													row.before.kind === 'removed'
														? '-'
														: row.before.kind === 'omitted'
															? '...'
															: '';
												const afterSign =
													row.after.kind === 'added'
														? '+'
														: row.after.kind === 'omitted'
															? '...'
															: '';

												return (
													<tr
														// eslint-disable-next-line react/no-array-index-key
														key={`${row.kind}-${index}`}
													>
														<td className={classes.diffLineNumber}>
															{row.before.lineNumber ?? ''}
														</td>
														<td
															className={`${classes.diffContent} ${beforeCellClassName ?? ''}`}
														>
															{beforeSign && (
																<span className={classes.diffSign}>
																	{beforeSign}{' '}
																</span>
															)}
															{row.before.content}
														</td>
														<td
															className={`${classes.diffLineNumber} ${classes.diffTableDivider}`}
														>
															{row.after.lineNumber ?? ''}
														</td>
														<td
															className={`${classes.diffContent} ${afterCellClassName ?? ''}`}
														>
															{afterSign && (
																<span className={classes.diffSign}>
																	{afterSign}{' '}
																</span>
															)}
															{row.after.content}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</Box>
							</Stack>
						</Card>
					)}

					<Stack spacing="sm">
						<Group position="apart">
							<Title order={4}>Memory Content</Title>
							<Group>
								<Button
									leftIconName="download"
									variant="default"
									size="md"
									onClick={handleDownloadMemory}
								>
									Download
								</Button>
								<Button
									variant="primary"
									size="md"
									onClick={handleSaveMemory}
									loading={saveMemoryMutation.isPending}
									disabled={!hasMemoryEdits || saveMemoryMutation.isPending}
								>
									Save
								</Button>
							</Group>
						</Group>
						<Textarea
							aria-label="Memory Content"
							className={classes.memoryEditor}
							minRows={16}
							value={editedMemory}
							onChange={(event) => {
								const nextMemory = event.currentTarget.value;
								setEditedMemoryDraft(
									nextMemory === memoryData.memory ? undefined : nextMemory
								);
							}}
						/>
					</Stack>
				</Stack>
			)}
		</Stack>
	);
}

export default InspectWorkspaceMemoryPage;
